import { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'

/**
 * Auth.jsx — BECivitas Login Page
 * Google OAuth (primary) + email/password fallback.
 * redirectTo uses window.location.origin — works on localhost AND becivitas.org.
 */
export default function Auth() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [mode, setMode] = useState('signin')
  const [agreementAccepted, setAgreementAccepted] = useState(false)

  const navy = '#0F2A4A'
  const gold = '#D4AF37'
  const silver = '#64748B'
  const white = '#FFFFFF'

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/callback` },
    })
    if (error) { setError(error.message); setLoading(false) }
  }

  const handleEmailAuth = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      if (mode === 'signup') {
        if (!agreementAccepted) {
          throw new Error('Please acknowledge the Participation Agreement to create an account.')
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
            data: {
              beta_agreement_accepted: true,
              beta_agreement_accepted_at: new Date().toISOString(),
            },
          },
        })
        if (error) throw error
        setError('Check your email for a confirmation link.')
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
        window.location.href = '/'
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: 24 }}>
      <div style={{ background: white, borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: navy }}>BE<span style={{ color: gold }}>Civitas</span></div>
          <div style={{ color: silver, fontSize: 14, marginTop: 6 }}>{mode === 'signup' ? 'Create your account' : 'Sign in to your account'}</div>
        </div>

        <button onClick={handleGoogleSignIn} disabled={loading}
          style={{ width: '100%', padding: '12px 0', border: '1.5px solid #E2E8F0', borderRadius: 10, background: white, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 15, fontWeight: 600, color: navy, marginBottom: 20, opacity: loading ? 0.6 : 1 }}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
          </svg>
          Sign in with Google
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
          <span style={{ color: silver, fontSize: 12 }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
        </div>

        <p style={{ textAlign: 'center', marginBottom: 20 }}><Link to="/questionnaire">Continue without an account</Link></p>
        <form onSubmit={handleEmailAuth}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: navy, marginBottom: 6 }}>Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
              style={{ width: '100%', padding: '10px 14px', boxSizing: 'border-box', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 14, color: navy, outline: 'none' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: navy, marginBottom: 6 }}>Password</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
              style={{ width: '100%', padding: '10px 14px', boxSizing: 'border-box', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 14, color: navy, outline: 'none' }} />
          </div>

          {mode === 'signup' && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: silver, fontSize: 12, marginBottom: 16 }}>
              <input type="checkbox" checked={agreementAccepted} onChange={e => setAgreementAccepted(e.target.checked)} />
              <span>I have read and agree to the BECivitas Participation Agreement. You will be able to review it again after confirming your email.</span>
            </label>
          )}

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', color: '#B91C1C', fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}

          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: '12px 0', background: navy, color: white, border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Please wait...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: silver }}>
          {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(null) }}
            style={{ background: 'none', border: 'none', color: gold, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            {mode === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  )
}
